import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import * as L from 'leaflet';
import { ToastrService } from 'ngx-toastr';

import {
  ADD_ONCE_STATUSES,
  ALL_STATUSES,
  ApiResponse,
  LadingStatusTimeline,
  StatusTimelinePayload,
} from '../../models/modal-system.interfaces';
import { ModalDialogData } from '../../services/carrier-orders-grid/grid-modal-dispatcher.service';

interface StatusOption { value: string; disabled: boolean; reason: string; }
interface MapPoint { id: string; status: string; lat: number; lng: number; time: string; }

@Component({
  selector: 'app-status-timeline-modal',
  templateUrl: './status-timeline-modal.component.html',
  styleUrls: ['./status-timeline-modal.component.scss'],
})
export class StatusTimelineModalComponent implements OnInit {
  readonly API_URL = '/api/dynamic_grids/lading_status_timelines.php';
  readonly MAP_API_URL = '/api/dynamic_grids/lading_map_data.php'; 

  events: LadingStatusTimeline[] = [];
  statusOptions: StatusOption[] = [];
  addForm!: FormGroup;
  editForm!: FormGroup;
  
  loading = false;
  saving = false;
  editingUuid: string | null = null;
  private existingStatuses = new Set<string>();

  // Map variables
  private map: L.Map | undefined;
  mapDataLoaded = false;
  mapPoints: MapPoint[] = [];

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StatusTimelineModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDialogData,
  ) {}

  ngOnInit(): void {
    this.addForm = this.fb.group({
      lading_status: ['', [Validators.required]],
      local_time: [''],
      time_zone_id: [''],
      lading_status_note: [''],
    });
    
    this.editForm = this.fb.group({
      lading_status: ['', [Validators.required]],
      local_time: [''],
      time_zone_id: [''],
      lading_status_note: [''],
    });
    
    this.loadTimeline();
  }

  // --- Map Logic ---

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 1) { 
      this.loadMapData();
    }
  }

  private loadMapData(): void {
    if (this.mapDataLoaded) {
      setTimeout(() => this.map?.invalidateSize(), 100);
      return;
    }

    this.http.post<{success: boolean, data: MapPoint[]}>(this.MAP_API_URL, {
      lading_uuid: this.data.lading_uuid
    }).subscribe({
      next: (res) => {
        this.mapDataLoaded = true;
        if (res.success && res.data.length > 0) {
          this.mapPoints = res.data;
          setTimeout(() => this.initMap(), 100); 
        }
      },
      error: () => {
        this.mapDataLoaded = true; // Prevents continuous loading spin
        this.toastr.error('Failed to load map coordinates');
      }
    });
  }

  private initMap(): void {
    if (this.map) {
      this.map.invalidateSize();
      return;
    }

    this.map = L.map('timeline-map').setView([39.8283, -98.5795], 4);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    const latLngs: L.LatLngExpression[] = [];

    this.mapPoints.forEach((point, index) => {
      latLngs.push([point.lat, point.lng]);

      const isLast = index === this.mapPoints.length - 1;
      const dotClass = isLast ? 'map-dot-active' : 'map-dot-history';
      
      const customIcon = L.divIcon({
        className: 'custom-leaflet-icon',
        html: `<div class="${dotClass}"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
      });

      const popupContent = `
        <div class="map-popup">
          <strong>${point.status}</strong><br>
          <span class="text-muted">${this.formatTimestamp(point.time)}</span>
        </div>
      `;

      L.marker([point.lat, point.lng], { icon: customIcon })
        .addTo(this.map!)
        .bindPopup(popupContent);
    });

    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#3b82f6', weight: 3, dashArray: '5, 5' }).addTo(this.map);
    }

    if (latLngs.length > 0) {
      this.map.fitBounds(L.latLngBounds(latLngs), { padding: [50, 50] });
    }
  }

  // --- Existing Logic ---

  loadTimeline(): void {
    this.loading = true;
    this.http.post<ApiResponse<LadingStatusTimeline[]>>(this.API_URL, {
      action: 'list', lading_uuid: this.data.lading_uuid,
    } as StatusTimelinePayload).subscribe({
      next: (r) => {
        this.loading = false;
        if (!r?.success) { this.toastr.error(r?.message ?? 'Failed to load timeline.'); return; }
        this.events = (r.data ?? []).sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        this.rebuildExistingStatuses();
        this.rebuildStatusOptions();
      },
      error: () => { this.loading = false; this.toastr.error('Network error.'); },
    });
  }

  private rebuildExistingStatuses(): void {
    this.existingStatuses.clear();
    this.events.forEach(e => { if (ADD_ONCE_STATUSES.has(e.lading_status)) this.existingStatuses.add(e.lading_status); });
  }

  private rebuildStatusOptions(): void {
    this.statusOptions = ALL_STATUSES.map(s => {
      const blocked = ADD_ONCE_STATUSES.has(s) && this.existingStatuses.has(s);
      return { value: s, disabled: blocked, reason: blocked ? 'Already exists' : '' };
    });
  }

  isAddOnceBlocked(status: string): boolean {
    return ADD_ONCE_STATUSES.has(status) && this.existingStatuses.has(status);
  }

  addEvent(): void {
    if (this.addForm.invalid) return;
    const v = this.addForm.getRawValue();
    if (this.isAddOnceBlocked(v.lading_status)) { this.toastr.warning(`"${v.lading_status}" already exists.`); return; }
    this.saving = true; 
    this.http.post<ApiResponse>(this.API_URL, {
      action: 'create', lading_uuid: this.data.lading_uuid,
      lading_status: v.lading_status, lading_status_note: v.lading_status_note || null,
      local_time: v.local_time || null, time_zone_id: v.time_zone_id || null,
    } as StatusTimelinePayload).subscribe({
      next: (r) => { 
        this.saving = false; 
        if (!r?.success) { this.toastr.error(r?.message ?? 'Failed.'); return; } 
        this.toastr.success('Event added.'); this.addForm.reset(); this.loadTimeline(); 
        this.mapDataLoaded = false; // Reset map to force fresh fetch
      },
      error: () => { this.saving = false; this.toastr.error('Network error.'); },
    });
  }

  startEdit(evt: LadingStatusTimeline): void {
    this.editingUuid = evt.lading_status_timeline_uuid;
    this.editForm.patchValue({ lading_status: evt.lading_status, local_time: evt.local_time ?? '', time_zone_id: evt.time_zone_id ?? '', lading_status_note: evt.lading_status_note ?? '' });
  }

  cancelEdit(): void { this.editingUuid = null; this.editForm.reset(); }

  saveEdit(): void {
    if (this.editForm.invalid || !this.editingUuid) return;
    const v = this.editForm.getRawValue();
    this.saving = true; 
    this.http.post<ApiResponse>(this.API_URL, {
      action: 'update', lading_uuid: this.data.lading_uuid,
      lading_status_timeline_uuid: this.editingUuid,
      lading_status: v.lading_status, lading_status_note: v.lading_status_note || null,
      local_time: v.local_time || null, time_zone_id: v.time_zone_id || null,
    } as StatusTimelinePayload).subscribe({
      next: (r) => { 
        this.saving = false; 
        if (!r?.success) { this.toastr.error(r?.message ?? 'Failed.'); return; } 
        this.toastr.success('Updated.'); this.editingUuid = null; this.editForm.reset(); this.loadTimeline(); 
        this.mapDataLoaded = false;
      },
      error: () => { this.saving = false; this.toastr.error('Network error.'); },
    });
  }

  deleteEvent(evt: LadingStatusTimeline): void {
    if (!confirm(`Delete "${evt.lading_status}"?`)) return;
    this.saving = true; 
    this.http.post<ApiResponse>(this.API_URL, {
      action: 'delete', lading_uuid: this.data.lading_uuid,
      lading_status_timeline_uuid: evt.lading_status_timeline_uuid,
    } as StatusTimelinePayload).subscribe({
      next: (r) => { 
        this.saving = false; 
        if (!r?.success) { this.toastr.error(r?.message ?? 'Failed.'); return; } 
        this.toastr.info('Deleted.'); this.loadTimeline(); 
        this.mapDataLoaded = false;
      },
      error: () => { this.saving = false; this.toastr.error('Network error.'); },
    });
  }

  close(): void { this.dialogRef.close({ refreshGrid: true }); }

  formatTimestamp(ts: string | null): string {
    if (!ts) return '—';
    const d = new Date(ts);
    return isNaN(d.getTime()) ? ts : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  get dialogTitle(): string { return this.data.modalConfig.dialog_title ?? this.data.headerName ?? 'Status Timeline'; }
}